<script setup lang="ts">
import { computed } from 'vue'
import { numBoundary } from '@/helpers/newHelpers'

export interface StatChartProps {
  points?: number[]
}

export type Point = {
  x: number
  y: number
}

const props = defineProps<StatChartProps>()

const svgHeight = 1
const svgWidth = 1

const scale = 1
const lineEndX = svgWidth * scale

const chartData = computed(() => {
  const defaultData = {
    linePath: '',
    circlePosition: { x: 0, y: 0 } as Point,
    topBoundary: { value: 0, y: 0 },
    bottomBoundary: { value: 0, y: svgHeight },
    clipPath: '',
  }
  if (!props.points?.length) return defaultData

  const maxValue = Math.max(...props.points)
  const minValue = Math.min(...props.points)
  const scaleX = (svgWidth * scale) / (props.points.length - 1)
  const scaleY = (svgHeight * scale) / (maxValue - minValue)

  const calculateY = (p: number) => svgHeight - (p - minValue) * scaleY

  const topBoundary = numBoundary(maxValue * 100, 10, 'up')
  const topBoundaryY = calculateY(topBoundary)
  const bottomBoundary = numBoundary(minValue * 100, 10, 'down')
  const bottomBoundaryY = calculateY(bottomBoundary)

  const generateSvgPath = (points: Point[]) =>
    points.map((point, i) => `${i === 0 ? 'M' : ''} ${point.x},${point.y}`).join(' ')

  // remove everything bellow the bottom line (keep only clip-path => above the bottom line)
  const clipPathCoords = [
    { x: 0, y: 0 },
    { x: 0, y: bottomBoundaryY + 1 },
    { x: lineEndX, y: bottomBoundaryY + 1 },
    { x: svgWidth, y: svgHeight },
    { x: svgWidth, y: -5 },
  ]

  // Calculate scaled points based on the input points
  const scaledPoints = props.points.map((point, i) => {
    const x = i * scaleX
    const y = calculateY(point)
    return { x, y }
  })

  // Transform scaledPoints into SVG path
  const linePath = generateSvgPath(scaledPoints)

  // Use the last scaled point for the circle's position
  const circlePosition =
    scaledPoints.length > 0 ? scaledPoints[scaledPoints.length - 1] : { x: 0, y: 0 }

  return {
    linePath,
    circlePosition,
    topBoundary: {
      value: topBoundary,
      y: topBoundaryY,
    },
    bottomBoundary: {
      value: bottomBoundary,
      y: bottomBoundaryY,
    },
    clipPath: generateSvgPath(clipPathCoords),
  }
})
</script>

<template>
  <div class="f-column mw-0 mh-0 position-relative svgchart">
    <div
      class="w-100 position-absolute"
      style="top: 0; left: 0"
    >
      <div
        class="d-flex w-100"
        style="position: absolute"
      >
        <div class="bt-grey w-100"></div>
        <div
          class="clr-grey pl-2 text-small"
          style="position: relative; transform: translate(0, -50%)"
        >
          {{ chartData.topBoundary.value }}
        </div>
      </div>
    </div>
    <div
      class="w-100 position-absolute"
      style="bottom: 0; left: 0"
    >
      <div
        class="d-flex w-100"
        style="position: absolute"
      >
        <div class="bt-grey w-100"></div>
        <div
          class="clr-grey pl-2 text-small"
          style="position: relative; transform: translate(0, -50%)"
        >
          {{ chartData.bottomBoundary.value }}
        </div>
      </div>
    </div>
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      style="margin-left: -1.25rem"
    >
      <path
        :d="chartData.linePath"
        fill="none"
        stroke="var(--clr-primary)"
        stroke-width="0.5"
        vector-effect="non-scaling-stroke"
      />

      <path
        :d="`M ${chartData.circlePosition.x} ${chartData.circlePosition.y} l 0.0001 0`"
        vector-effect="non-scaling-stroke"
        class="svgchart_hands_circle svgchart_hands_circle_ip"
      />
    </svg>
  </div>
</template>

<style lang="scss" scoped>
svg {
  display: block;
  background-clip: content-box, padding-box;
  overflow: visible;
  position: relative;
  z-index: 1;
}
.svgchart {
  --stroke-unit: 1;
  --circle-stroke: clamp(5px, calc(6px * var(--stroke-unit)), 22px);
  &_hands {
    &_circle {
      stroke-linecap: round;
      stroke-width: var(--circle-stroke);
      z-index: 1;
      &_ip {
        stroke: var(--clr-primary);
      }
    }
  }
}
</style>
